import { Chip, Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { Icon } from "src/components";
import { OptionCombinationsChip_TakeoffLineItemFragment } from "./OptionCombinationsChip.generated";

type OptionCombinationsChipProps = {
  tli: OptionCombinationsChip_TakeoffLineItemFragment;
  showMissingCombinations?: boolean;
};

export function OptionCombinationsChip({ tli, showMissingCombinations = false }: OptionCombinationsChipProps) {
  const combinations = showMissingCombinations ? tli.missingCombinations : tli.targetCombinations;
  if (combinations.isEmpty) return null;
  // If tli is a placeholder, use its groups, otherwise look up to the placeholder
  const targetGroups = tli.targetGroups.nonEmpty ? tli.targetGroups : (tli.placeholder?.targetGroups ?? []);
  return (
    <Tooltip
      xss={Css.p0.$}
      title={
        <OptionCombinationsChipTooltipContent
          combinations={combinations}
          showMissingCombinations={showMissingCombinations}
        />
      }
    >
      <Chip
        text={`${combinations.length} ${targetGroups
          .sortBy((rpog) => rpog.order)
          .map((rpog) => rpog.name)
          .join(" / ")}`}
      />
    </Tooltip>
  );
}

type OptionCombinationsChipTooltipContentProps = {
  combinations: { id: string; name: string }[];
  showMissingCombinations: boolean;
};

export function OptionCombinationsChipTooltipContent({
  combinations,
  showMissingCombinations,
}: OptionCombinationsChipTooltipContentProps) {
  const tid = useTestIds({});
  const combinationsLimit = 15; // Tooltips aren't scrollable, so showing too many combinations isn't practical.
  const visibleCombinations = combinations.slice(0, combinationsLimit);
  const remainingCount = combinations.length - visibleCombinations.length;

  return (
    <div {...tid.combinationDetailsTooltip} css={Css.gray900.sm.$}>
      <div css={Css.pyPx(14).pl2.pr4.df.bgWhite.if(showMissingCombinations).bgRed100.$}>
        <div css={Css.pr1.$}>
          {showMissingCombinations ? (
            <Icon icon="error" color={Palette.Red600} inc={2.5} />
          ) : (
            <Icon icon="checkCircle" color={Palette.Green600} inc={2.5} />
          )}
        </div>
        <div>
          <div>
            {combinations.length} {showMissingCombinations ? "Missing" : "Added"}:
          </div>
          {visibleCombinations.map((tc) => (
            <div key={tc.id} css={Css.pyPx(6).$}>
              {tc.name}
            </div>
          ))}
          {remainingCount > 0 && <div css={Css.pyPx(6).$}>and {remainingCount} more...</div>}
        </div>
      </div>
    </div>
  );
}
